<template>
  <div class="charts-row">
    <!-- Content Growth Chart -->
    <div class="chart-card">
      <div class="card-header">
        <div class="title-box">
          <i class="far fa-map header-icon icon-blue"></i>
          <div>
            <h3 class="chart-title">Tăng trưởng nội dung</h3>
            <p class="chart-subtitle">
              Số lượng trang di sản, blogs, địa điểm phát triển trong CSDL
            </p>
          </div>
        </div>
      </div>
      <div class="chart-body">
        <apexchart
          v-if="hasContentData"
          type="bar"
          height="280"
          :options="contentChartOptions"
          :series="contentChartSeries"
        ></apexchart>
        <el-empty v-else description="Không có dữ liệu nội dung"></el-empty>
      </div>
    </div>

    <!-- Country Share Chart -->
    <div class="chart-card">
      <div class="card-header">
        <div class="title-box">
          <i class="fas fa-globe header-icon icon-indigo"></i>
          <div>
            <h3 class="chart-title">Phân bố khách theo quốc gia</h3>
            <p class="chart-subtitle">
              Số lượng người dùng hoạt động theo quốc gia ({{ rangeLabel }})
            </p>
          </div>
        </div>
      </div>
      <div class="chart-body">
        <apexchart
          v-if="hasCountryData"
          type="bar"
          height="280"
          :options="countryChartOptions"
          :series="countryChartSeries"
        ></apexchart>
        <el-empty v-else description="Không có dữ liệu quốc gia"></el-empty>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'ContentCountryCharts',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    contentGrowth: {
      type: Array,
      default: () => []
    },
    countryShare: {
      type: Array,
      default: () => []
    },
    rangeLabel: {
      type: String,
      default: '30 phút qua'
    }
  },
  computed: {
    hasContentData() {
      return this.contentGrowth && this.contentGrowth.length > 0
    },
    hasCountryData() {
      return this.countryShare && this.countryShare.length > 0
    },

    // 1. Content Growth Chart
    contentChartSeries() {
      return [
        {
          name: 'Bài viết Blog',
          data: this.contentGrowth.map((c) => c.blogs || 0)
        },
        {
          name: 'Trang di sản',
          data: this.contentGrowth.map((c) => c.pages || 0)
        },
        {
          name: 'Địa điểm bản đồ',
          data: this.contentGrowth.map((c) => c.locations || 0)
        }
      ]
    },
    contentChartOptions() {
      const categories = this.contentGrowth.map((c) => c.month || '')
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'inherit'
        },
        colors: ['#eab308', '#6366f1', '#10b981'],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            borderRadius: 4
          }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories,
          labels: {
            style: { colors: '#9ca3af', fontSize: '11px', fontWeight: 500 }
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: {
          labels: {
            style: { colors: '#9ca3af', fontSize: '11px' }
          }
        },
        grid: {
          borderColor: '#f3f4f6',
          strokeDashArray: 4
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'center',
          fontSize: '13px',
          fontWeight: 600,
          markers: { radius: 12 }
        },
        tooltip: {
          theme: 'light'
        }
      }
    },

    // 2. Country Share Horizontal Bar Chart
    countryChartSeries() {
      return [
        {
          name: 'Người dùng',
          data: this.countryShare.map((c) => c.count || 0)
        }
      ]
    },
    countryChartOptions() {
      const categories = this.countryShare.map((c) => c.country || '')
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'inherit'
        },
        colors: ['#6366f1'],
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '40%',
            borderRadius: 6
          }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories,
          labels: {
            style: { colors: '#9ca3af', fontSize: '11px' }
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: {
          labels: {
            style: { colors: '#374151', fontSize: '12px', fontWeight: 500 }
          }
        },
        grid: {
          borderColor: '#f3f4f6',
          strokeDashArray: 4
        },
        legend: { show: false },
        tooltip: {
          theme: 'light',
          y: {
            formatter(val) {
              return `${val} người dùng`
            }
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.charts-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
}

.chart-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
  border: 1px solid #f1f3f5;

  .card-header {
    margin-bottom: 20px;

    .title-box {
      display: flex;
      align-items: flex-start;
      gap: 10px;

      .header-icon {
        font-size: 16px;
        margin-top: 3px;
        &.icon-blue { color: #3b82f6; }
        &.icon-indigo { color: #6366f1; }
      }

      .chart-title {
        font-size: 16px;
        font-weight: 700;
        color: #1f2937;
        margin: 0;
      }

      .chart-subtitle {
        font-size: 13px;
        color: #6b7280;
        margin: 4px 0 0 0;
      }
    }
  }

  .chart-body {
    min-height: 280px;
  }
}
</style>
